import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Download, FileText, Sparkles, AlertTriangle, ChevronDown } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export const DOC_NAMES = [
  "P&I", "H&M", "Registry Certificate", "Load Line", "Safety Management Certificate", "ISM Certificate",
  "ISPS Certificate", "MLC Certificate", "Tonnage Certificate", "Crew List", "Port State Control",
  "Class Certificate", "IOPP Certificate", "Garbage Management Plan", "Ballast Water Management Plan",
  "Fire Safety Certificate", "Life Saving Appliances", "Radio Certificate", "Manning Certificate", "Bill of Sale",
];
const CUSTOM = "__custom__";

export const EXPIRY_META = {
  missing: { label: "Date Missing", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  expired: { label: "EXPIRED", cls: "bg-red-900 text-white border-red-900" },
  red: { label: "Under 1 year", cls: "bg-red-50 text-red-700 border-red-200" },
  orange: { label: "1–2 years", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  green: { label: "Over 2 years", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export function ExpiryBadge({ status, testid }) {
  const m = EXPIRY_META[status] || EXPIRY_META.missing;
  return <span data-testid={testid} className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-semibold whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

function FilePicker({ vesselId, upload, setUpload, onExtracted, required }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pdf|jpe?g)$/i.test(file.name)) { toast.error("Only PDF or JPG files are allowed"); e.target.value = ""; return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File is larger than 10 MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/vessels/${vesselId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload(data);
      onExtracted(data.expiry_date);
    } catch (err) {
      toast.error(formatApiError(err));
      e.target.value = "";
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <Label htmlFor="doc-file">Document file (PDF or JPG){!required && <span className="text-slate-400 font-normal"> — optional, replaces current file</span>}</Label>
      <Input id="doc-file" type="file" accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg" onChange={pick} disabled={busy} data-testid="document-file-input" />
      {busy && (
        <div className="flex items-center gap-2 text-xs text-cyan-700" data-testid="document-ai-reading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading and reading the expiry date with AI…
        </div>
      )}
      {!busy && upload && (upload.found ? (
        <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="document-ai-found">
          <Sparkles className="w-3.5 h-3.5" /> AI found expiry date {upload.expiry_date}. Check it below and correct it if needed.
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-amber-700" data-testid="document-ai-not-found">
          <AlertTriangle className="w-3.5 h-3.5" /> AI could not find an expiry date. Please enter it manually.
        </div>
      ))}
    </div>
  );
}

function DocumentDialog({ open, onOpenChange, vesselId, doc, onSaved }) {
  const [form, setForm] = useState({ choice: "", custom: "", issue_date: "", expiry_date: "" });
  const [upload, setUpload] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const known = doc && DOC_NAMES.includes(doc.document_name);
    setForm({
      choice: doc ? (known ? doc.document_name : CUSTOM) : "",
      custom: doc && !known ? doc.document_name : "",
      issue_date: doc?.issue_date || "",
      expiry_date: doc?.expiry_date || "",
    });
    setUpload(null);
  }, [open, doc]);

  const submit = async (e) => {
    e.preventDefault();
    const name = form.choice === CUSTOM ? form.custom.trim() : form.choice;
    if (!name) { toast.error("Choose or type a document name"); return; }
    if (!doc && !upload) { toast.error("Upload the document file first"); return; }
    setSaving(true);
    const body = { document_name: name, issue_date: form.issue_date || null, expiry_date: form.expiry_date || null };
    if (upload) body.upload_id = upload.upload_id;
    try {
      if (doc) await api.patch(`/documents/${doc.id}`, body);
      else await api.post(`/vessels/${vesselId}/documents`, body);
      toast.success(doc ? "Document updated" : "Document added");
      if (!body.expiry_date) toast.warning("No expiry date set — this document will show as Date Missing");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="document-form-dialog">
        <DialogHeader>
          <DialogTitle>{doc ? "Edit document" : "Add document"}</DialogTitle>
          <DialogDescription>AI reads the expiry date from the file. You can always correct it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Document name</Label>
            <Select value={form.choice} onValueChange={(v) => setForm({ ...form, choice: v })}>
              <SelectTrigger data-testid="document-name-select"><SelectValue placeholder="Select document" /></SelectTrigger>
              <SelectContent>
                {DOC_NAMES.map((n) => <SelectItem key={n} value={n} data-testid={`document-name-option-${n}`}>{n}</SelectItem>)}
                <SelectItem value={CUSTOM} data-testid="document-name-option-custom">+ Add custom name</SelectItem>
              </SelectContent>
            </Select>
            {form.choice === CUSTOM && (
              <Input value={form.custom} onChange={(e) => setForm({ ...form, custom: e.target.value })} placeholder="Type the document name"
                required data-testid="document-custom-name-input" />
            )}
          </div>
          <FilePicker vesselId={vesselId} upload={upload} setUpload={setUpload} required={!doc}
            onExtracted={(d) => setForm((f) => ({ ...f, expiry_date: d || (doc ? f.expiry_date : "") }))} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="doc-issue">Issue date</Label>
              <Input id="doc-issue" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="document-issue-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-expiry">Expiry date</Label>
              <Input id="doc-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="document-expiry-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="document-form-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : doc ? "Save changes" : "Add document"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function downloadFile(d) {
  try {
    const { data } = await api.get(`/documents/${d.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = d.file_name || "document";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    toast.error("Could not download the file");
  }
}

function DocActions({ d, canManage, onEdit, onDelete, suffix = "" }) {
  if (!canManage) return null;
  return (
    <>
      <Button size="icon" variant="ghost" onClick={() => onEdit(d)} data-testid={`document-edit-btn${suffix}-${d.id}`}><Pencil className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => onDelete(d)} data-testid={`document-delete-btn${suffix}-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
    </>
  );
}

function MobileDetails({ d, canManage, onEdit, onDelete }) {
  return (
    <tr className="md:hidden bg-slate-50/60" data-testid={`document-mobile-details-${d.id}`}>
      <td colSpan={2} className="px-4 pb-3 pt-1">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div><dt className="text-slate-400">Expiry date</dt><dd className="font-mono text-slate-700">{d.expiry_date || "—"}</dd></div>
          <div><dt className="text-slate-400">Days left</dt><dd className={`font-mono ${d.days_left < 0 ? "text-red-800 font-semibold" : "text-slate-700"}`}>{d.days_left ?? "—"}</dd></div>
          <div className="col-span-2"><dt className="text-slate-400">Uploaded by</dt><dd className="text-slate-700">{d.uploaded_by_name || "—"}</dd></div>
        </dl>
        <div className="flex items-center gap-1 mt-2 -ml-2">
          <Button size="sm" variant="ghost" onClick={() => downloadFile(d)} data-testid={`document-download-btn-mobile-${d.id}`}><Download className="w-3.5 h-3.5 mr-1" /> File</Button>
          <DocActions d={d} canManage={canManage} onEdit={onEdit} onDelete={onDelete} suffix="-mobile" />
        </div>
      </td>
    </tr>
  );
}

function DocumentRow({ d, canManage, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const cell = "px-5 py-3.5 hidden md:table-cell";
  return (
    <>
    <tr className="border-t border-slate-100" data-testid={`document-row-${d.id}`}>
      <td className="px-4 md:px-5 py-3.5">
        <button type="button" onClick={() => setOpen(!open)} className="md:pointer-events-none text-left flex items-start gap-1.5 min-w-0" data-testid={`document-expand-btn-${d.id}`}>
          <ChevronDown className={`w-4 h-4 mt-0.5 shrink-0 text-slate-400 md:hidden transition-transform ${open ? "rotate-180" : ""}`} />
          <span className="min-w-0">
            <span className="block font-semibold text-slate-800 break-words [overflow-wrap:anywhere]" data-testid={`document-name-${d.id}`}>{d.document_name}</span>
            {d.extracted_by_ai && <span className="text-[11px] text-cyan-700 flex items-center gap-1 mt-0.5" data-testid={`document-ai-flag-${d.id}`}><Sparkles className="w-3 h-3" /> Date read by AI</span>}
          </span>
        </button>
      </td>
      <td className={`${cell} font-mono text-slate-700 whitespace-nowrap`} data-testid={`document-expiry-${d.id}`}>{d.expiry_date || "—"}</td>
      <td className={`${cell} font-mono whitespace-nowrap`} data-testid={`document-days-left-${d.id}`}>
        {d.days_left === null ? <span className="text-slate-400">—</span> : <span className={d.days_left < 0 ? "text-red-800 font-semibold" : "text-slate-700"}>{d.days_left}</span>}
      </td>
      <td className="px-4 md:px-5 py-3.5 text-right md:text-left"><ExpiryBadge status={d.expiry_status} testid={`document-status-${d.id}`} /></td>
      <td className={`${cell} text-slate-600 whitespace-nowrap`} data-testid={`document-uploaded-by-${d.id}`}>{d.uploaded_by_name || "—"}</td>
      <td className={cell}>
        <Button size="sm" variant="ghost" onClick={() => downloadFile(d)} data-testid={`document-download-btn-${d.id}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> File
        </Button>
      </td>
      <td className={`${cell} text-right whitespace-nowrap`}>
        <DocActions d={d} canManage={canManage} onEdit={onEdit} onDelete={onDelete} />
      </td>
    </tr>
    {open && <MobileDetails d={d} canManage={canManage} onEdit={onEdit} onDelete={onDelete} />}
    </>
  );
}

const FILTERS = [["all", "Show All"], ["expired", "Expired Only"], ["expiring_1y", "Expiring within 1 Year"]];

export function VesselDocuments({ vesselId }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("documents.manage");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState({ open: false, doc: null });
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vessels/${vesselId}/documents`, { params: { filter } });
      setDocs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vesselId, filter]);

  const remove = async () => {
    const d = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="vessel-documents">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5 bg-white border border-slate-200 rounded-lg p-1" data-testid="document-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`document-filter-${k}`}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${filter === k ? "bg-[#0b192c] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {l}
            </button>
          ))}
        </div>
        {canManage && (
          <Button onClick={() => setDialog({ open: true, doc: null })} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="add-document-btn">
            <Plus className="w-4 h-4 mr-1" /> Add document
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="documents-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                {[["Doc Name", true], ["Expiry Date"], ["Days Left"], ["Status", true], ["Uploaded By"], ["File"], [""]].map(([h, always]) => (
                  <th key={h} className={`px-4 md:px-5 py-3 font-medium whitespace-nowrap ${always ? "" : "hidden md:table-cell"} ${h === "Status" ? "text-right md:text-left" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && docs.map((d) => (
                <DocumentRow key={d.id} d={d} canManage={canManage} onEdit={(doc) => setDialog({ open: true, doc })} onDelete={setToDelete} />
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
        {!loading && docs.length === 0 && (
          <div className="py-12 text-center" data-testid="documents-empty-state">
            <FileText className="w-8 h-8 text-slate-300 mx-auto" />
            <div className="text-sm text-slate-500 mt-2">{filter === "all" ? "No documents uploaded for this vessel yet." : "No documents match this filter."}</div>
          </div>
        )}
      </div>

      <DocumentDialog open={dialog.open} onOpenChange={(o) => setDialog((s) => ({ ...s, open: o }))} vesselId={vesselId} doc={dialog.doc} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="document-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.document_name}?</AlertDialogTitle>
            <AlertDialogDescription>The document will be removed from this vessel.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="document-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="document-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
